import { useState } from 'react'
import { Check } from 'lucide-react'
import { ErrorState, FileUploader, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useBranding, useBrandingMutations } from '../api'
import { useLegalEntities } from '@/features/org'
import { Select } from '@/components/ui/select'
import type { BrandingView } from '../types'

const HEX = /^#[0-9a-f]{6}$/i

/**
 * Branding, per legal entity as well as for the group.
 *
 * <p>An entity with no branding of its own inherits the group's, which is why every field can
 * be left empty: blank means "use the group's", not "use nothing". The preview shows what a
 * letterhead will actually carry, because a hex code in a text box tells nobody whether the
 * two colours work together.
 */
export function BrandingPage() {
  useBreadcrumb()
  const [entityId, setEntityId] = useState('')
  const branding = useBranding(entityId || undefined)
  const entities = useLegalEntities()

  return (
    <div className="space-y-6">
      <PageHeader
        title="Branding"
        subtitle="Logo, colours and letterhead — for the group, or overridden per entity."
        actions={
          <div className="space-y-1.5">
            <Label htmlFor="brand-entity">Entity</Label>
            {/* A picker, not a pasted id: a typed name reached the API as an unparseable UUID. */}
            <Select
              id="brand-entity"
              value={entityId}
              onChange={(event) => setEntityId(event.target.value)}
              className="w-72"
            >
              <option value="">Group default</option>
              {entities.data?.map((entity) => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </Select>
          </div>
        }
      />

      {branding.isPending && <Skeleton className="h-64" />}
      {branding.isError && <ErrorState error={branding.error} onRetry={() => void branding.refetch()} />}
      {branding.isSuccess && (
        // Remounted when the entity changes, so the form initialises from the branding that
        // was just loaded. Copying server data into state with an effect would fire a second
        // render on every load and discard whatever was half-typed when a refetch landed.
        <BrandingForm key={entityId} entityId={entityId} loaded={branding.data} />
      )}
    </div>
  )
}

function BrandingForm({ entityId, loaded }: { entityId: string; loaded: BrandingView }) {
  const { save } = useBrandingMutations()

  const [primary, setPrimary] = useState(loaded.primaryColor ?? '')
  const [accent, setAccent] = useState(loaded.accentColor ?? '')
  const [footer, setFooter] = useState(loaded.documentFooter ?? '')
  const [emailFooter, setEmailFooter] = useState(loaded.emailFooterHtml ?? '')
  const [logoFileId, setLogoFileId] = useState<string | null>(loaded.logoFileId)
  const [letterheadFileId, setLetterheadFileId] = useState<string | null>(loaded.letterheadFileId)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  async function submit() {
    setError(null)
    setSaved(false)
    try {
      await save.mutateAsync({
        legalEntityId: entityId || null,
        primaryColor: primary || null,
        accentColor: accent || null,
        documentFooter: footer || null,
        emailFooterHtml: emailFooter || null,
        logoFileId,
        letterheadFileId,
      })
      setSaved(true)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'Branding could not be saved')
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="space-y-4 p-5">
        <h2 className="text-sm font-semibold text-[var(--color-heading)]">Colours and text</h2>
        <p className="text-sm text-[var(--color-muted)]">
          Leave a field empty to inherit the group&apos;s setting rather than to clear it.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="brand-primary">Primary colour</Label>
            <div className="flex gap-2">
              <Input
                id="brand-primary" value={primary} placeholder="#5b46f0"
                onChange={(event) => setPrimary(event.target.value)}
              />
              <input
                type="color"
                aria-label="Pick the primary colour"
                value={HEX.test(primary) ? primary : '#5b46f0'}
                onChange={(event) => setPrimary(event.target.value)}
                className="h-10 w-12 shrink-0 rounded-[var(--radius-control)] border border-[var(--color-border)]"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="brand-accent">Accent colour</Label>
            <div className="flex gap-2">
              <Input
                id="brand-accent" value={accent} placeholder="#22c55e"
                onChange={(event) => setAccent(event.target.value)}
              />
              <input
                type="color"
                aria-label="Pick the accent colour"
                value={HEX.test(accent) ? accent : '#22c55e'}
                onChange={(event) => setAccent(event.target.value)}
                className="h-10 w-12 shrink-0 rounded-[var(--radius-control)] border border-[var(--color-border)]"
              />
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="brand-footer">Document footer</Label>
          <Input
            id="brand-footer" value={footer} placeholder="Your Company FZ-LLC · Dubai, UAE"
            onChange={(event) => setFooter(event.target.value)}
          />
          <p className="text-xs text-[var(--color-muted)]">Printed at the foot of generated PDFs.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="brand-email">Email footer</Label>
          <textarea
            id="brand-email" rows={3} value={emailFooter}
            onChange={(event) => setEmailFooter(event.target.value)}
            className="w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm"
          />
        </div>

        <div className="flex items-center gap-3">
          <Button type="button" disabled={save.isPending} onClick={() => void submit()}>
            {save.isPending ? 'Saving…' : 'Save branding'}
          </Button>
          {saved && (
            <span role="status" className="flex items-center gap-1 text-sm text-[var(--color-success)]">
              <Check className="size-4" aria-hidden="true" />
              Saved.
            </span>
          )}
        </div>

        {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}
      </Card>

      <div className="space-y-6">
        <Card className="space-y-3 p-5">
          <h2 className="text-sm font-semibold text-[var(--color-heading)]">Logo</h2>
          <FileUploader
            ownerType="branding"
            accept="image/png,image/jpeg,image/webp"
            onUploaded={(file) => setLogoFileId(file.id)}
          />
          {logoFileId && (
            <img
              src={`/api/v1/files/${logoFileId}`}
              alt="Current logo"
              className="max-h-16 rounded-[var(--radius-control)] border border-[var(--color-border)] p-2"
            />
          )}
        </Card>

        <Card className="space-y-3 p-5">
          <h2 className="text-sm font-semibold text-[var(--color-heading)]">Letterhead</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Used behind generated letters and payslips.
          </p>
          <FileUploader
            ownerType="branding"
            accept="image/png,image/jpeg,application/pdf"
            onUploaded={(file) => setLetterheadFileId(file.id)}
          />
        </Card>

        <Card className="space-y-3 p-5">
          <h2 className="text-sm font-semibold text-[var(--color-heading)]">Preview</h2>
          <div
            className="rounded-[var(--radius-control)] border border-[var(--color-border)] p-4"
            style={{ borderTopWidth: 4, borderTopColor: primary || 'var(--color-primary)' }}
          >
            {logoFileId && <img src={`/api/v1/files/${logoFileId}`} alt="" className="mb-3 max-h-10" />}
            <p className="text-sm font-semibold text-[var(--color-heading)]">
              {loaded.legalEntityName ?? 'Group default'}
            </p>
            <p className="mt-1 text-sm text-[var(--color-body)]">
              This is roughly how a generated letter will open.
            </p>
            <span
              className="mt-3 inline-block rounded-[var(--radius-control)] px-3 py-1 text-xs text-white"
              style={{ backgroundColor: accent || 'var(--color-success)' }}
            >
              Accent
            </span>
            {footer && (
              <p className="mt-4 border-t border-[var(--color-border)] pt-2 text-xs text-[var(--color-muted)]">
                {footer}
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
